import { useFormContext } from 'react-hook-form'
import { FundingModalFormValues } from 'core/widgets/funding-modal/FundingModal'
import Select from 'core/components/inputs/Select'
import { MouseEvent, useCallback, useMemo } from 'react'
import Icon from 'core/components/icons/Icon'
import { ID } from 'types/utility'
import cn from 'utils/cn'
import useIndexedPayPeriods from 'core/widgets/funding-modal/useIndexedPayPeriods'
import {
  formatPayPeriodLabel,
  getPayPeriodsTotal,
  getSelectedPayPeriodsFromIds
} from 'utils/Functions'
import numeral from 'numeral'
import EditAmountRadio from 'core/widgets/funding-modal/edit-amount/EditAmountRadio'
import SelectedPayPeriodsBrief from 'core/widgets/funding-modal/SelectedPayPeriodsBrief'
import Button from 'core/components/buttons/Button'

interface Props {
  className?: string
}

const SelectedPayPeriodsAmountType = ({ className }: Props) => {
  const { watch, setValue } = useFormContext<FundingModalFormValues>()
  const {
    payPeriods,
    indexedPayPeriods,
    isLoading: payPeriodsLoading
  } = useIndexedPayPeriods()
  const [
    selectedPayPeriodsMode,
    amountType,
    previewSelectedPayPeriods,
    selectedPayPeriodIds
  ] = watch([
    'selectPayPeriodsMode',
    'amountType',
    'previewSelectedPayPeriods',
    'selected_pay_periods'
  ])
  const isSelected = amountType === 'selected-pay-periods'
  const hasSelectedPayPeriods = previewSelectedPayPeriods.length > 0

  const [selectedPayPeriods, totalAmount] = useMemo(() => {
    const selectedPayPeriods = getSelectedPayPeriodsFromIds(
      indexedPayPeriods,
      selectedPayPeriodIds
    )
    const payPeriodsTotal = getPayPeriodsTotal(selectedPayPeriods)
    return [selectedPayPeriods, payPeriodsTotal]
  }, [indexedPayPeriods, selectedPayPeriodIds])

  const options = useMemo(() => {
    return payPeriods.map((payPeriod) => {
      const isSelected = previewSelectedPayPeriods.includes(payPeriod.id)

      return {
        token_id: payPeriod.id,
        label: (
          <div className="flex justify-between items-center gap-2">
            <span>{formatPayPeriodLabel(payPeriod, true)}</span>
            {isSelected && <Icon name="check" color="text-primary" size={15} />}
          </div>
        )
      }
    })
  }, [payPeriods, previewSelectedPayPeriods])

  const onSaveClick = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setValue('selected_pay_periods', previewSelectedPayPeriods)
      setValue('selectPayPeriodsMode', false)
    },
    [setValue, previewSelectedPayPeriods]
  )

  const onSelectPayPeriodClick = useCallback(() => {
    setValue('selectPayPeriodsMode', true)
  }, [setValue])

  const value = 'selected-pay-periods' as FundingModalFormValues['amountType']

  return (
    <EditAmountRadio
      className={className}
      value={value}
      label={
        <div className="w-full">
          {selectedPayPeriodsMode && isSelected ? (
            <div
              className="flex items-center gap-5"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
            >
              <Select
                disabled={payPeriodsLoading}
                autoCloseOnSelect={false}
                options={options}
                className={cn(
                  'h-[44px] w-[380px] [&_input]:py-2 [&_input]:leading-tight',
                  hasSelectedPayPeriods ? '[&_input]:!placeholder-black' : '',
                  isSelected && !payPeriodsLoading && !hasSelectedPayPeriods
                    ? '!outline-danger'
                    : ''
                )}
                onChange={(value) => {
                  if (previewSelectedPayPeriods.includes(value as ID)) {
                    setValue(
                      'previewSelectedPayPeriods',
                      previewSelectedPayPeriods.filter(
                        (payPeriodId) => payPeriodId !== value
                      )
                    )
                  } else {
                    setValue('previewSelectedPayPeriods', [
                      ...previewSelectedPayPeriods,
                      value as ID
                    ])
                  }
                }}
                getDisplayValue={() => {
                  if (hasSelectedPayPeriods) {
                    const firstPayPeriod = payPeriods.find((payPeriod) =>
                      previewSelectedPayPeriods.includes(payPeriod.id)
                    )
                    if (firstPayPeriod) {
                      return (
                        <div>
                          <span>
                            {formatPayPeriodLabel(firstPayPeriod, true)}
                          </span>
                          {!!previewSelectedPayPeriods &&
                            previewSelectedPayPeriods?.length > 1 && (
                              <span className="text-primary ml-1">
                                +{previewSelectedPayPeriods?.length - 1}
                              </span>
                            )}
                        </div>
                      )
                    }
                  }
                  return payPeriodsLoading
                    ? 'Loading pay periods...'
                    : 'Select at least one pay period'
                }}
              />
              <Button
                variant="link"
                className="text-primary cursor-pointer select-none"
                onClick={onSaveClick}
                disabled={previewSelectedPayPeriods.length === 0}
              >
                Save
              </Button>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center relative top-[2px]">
                <label
                  htmlFor={value}
                  className="cursor-pointer subtitle-2 font-semibold"
                >
                  Current pay period - {numeral(totalAmount).format('$0,0.00')}
                </label>
                {isSelected && (
                  <label
                    htmlFor={value}
                    className="text-primary cursor-pointer font-semibold"
                    onClick={onSelectPayPeriodClick}
                  >
                    Select pay period
                  </label>
                )}
              </div>
              <label
                htmlFor={value}
                className="text-sm text-dark-4 cursor-pointer inline-block relative -top-[2px]"
              >
                <SelectedPayPeriodsBrief
                  selectedPayPeriods={selectedPayPeriods}
                />
              </label>
            </>
          )}
        </div>
      }
    />
  )
}

export default SelectedPayPeriodsAmountType
